/*
 * Project: bellcloud_fe
 * File: src/config/pageDepthConfig.ts
 * Author: SWAN
 * Created: 2025-01-XX
 * Description: 페이지별 HierarchicalSelector depth 설정
 */

import type { SelectionDepth } from '@/components/common/HierarchicalSelector';

/**
 * 페이지별 필요한 depth 설정
 *
 * - null: HierarchicalSelector를 표시하지 않음
 * - 'company': Company까지만 표시
 * - 'site': Company + Site까지 표시
 * - 'device': Company + Site + Device 모두 표시
 */
interface PageDepthConfig {
  path: string; // 페이지 경로 (정규식 패턴)
  depth: SelectionDepth | null; // 필요한 depth (null이면 표시하지 않음)
  description?: string; // 설명
}

/**
 * 페이지별 depth 설정
 *
 * 우선순위: 배열의 앞쪽이 높음 (먼저 매칭되는 것 사용)
 */
const PAGE_DEPTH_CONFIG: PageDepthConfig[] = [
  // ==================== Dashboard ====================
  {
    path: '/',
    depth: 'site', // Admin: Company 선택, CompanyManager/SiteManager: Site 선택
    description: 'Dashboard Overview',
  },

  // ==================== Monitoring ====================
  {
    path: '/monitoring/realtime',
    depth: 'site', // Site까지만 필요 (실시간 모니터링은 사이트별)
    description: '실시간 모니터링',
  },
  {
    path: '/monitoring/playback',
    depth: 'device', // Device까지 필요 (히스토리는 기기별)
    description: '히스토리 모니터링',
  },

  // ==================== Configuration ====================
  {
    path: '/configurator/lite',
    depth: null, // 설정 페이지는 HierarchicalSelector 불필요 (직접 연결)
    description: 'Lite 설정',
  },
  {
    path: '/configurator/mini',
    depth: null, // 설정 페이지는 HierarchicalSelector 불필요 (직접 연결)
    description: 'Mini 설정',
  },

  // ==================== Datalog ====================
  {
    path: '/detaLog', // 실제 경로는 detaLog (오타지만 기존 경로 유지)
    depth: 'site', // Site까지만 필요 (데이터로그는 사이트 선택 후 기기 목록에서 선택)
    description: '데이터로그',
  },

  // ==================== Sites ====================
  {
    path: '/site', // 실제 경로는 /site
    depth: 'site', // Site까지만 필요 (사이트 관리)
    description: '사이트 관리',
  },

  // ==================== Management (Company + Site 통합) ====================
  {
    path: '/management',
    depth: null, // 탭 내부에서 직접 렌더링
    description: '관리 (Company/Site 탭)',
  },

  // ==================== Company ====================
  {
    path: '/company',
    depth: 'company', // Company까지만 필요 (회사 관리)
    description: '회사 관리',
  },

  // ==================== Admin ====================
  {
    path: '/admin/allowed-emails',
    depth: null, // Admin 페이지는 HierarchicalSelector 불필요
    description: '가입·구독 초대',
  },
  {
    path: '/admin/companies',
    depth: null,
    description: '회사·사용자 관리 (Admin)',
  },
  {
    path: '/admin/user-log',
    depth: null,
    description: 'Activity Log (자체 필터 사용)',
  },
  {
    path: '/admin/system-status',
    depth: null,
    description: 'System Status (Redis/Device connection status)',
  },

  // ==================== Charts (Demo) ====================
  {
    path: '/charts',
    depth: null, // 차트 데모는 HierarchicalSelector 불필요
    description: '차트 데모',
  },

  // ==================== Default ====================
  {
    path: '.*', // 기본값 (모든 경로)
    depth: 'device', // 기본적으로 Device까지 표시
    description: '기본값',
  },
];

/**
 * 현재 경로에 맞는 depth 가져오기
 * @param pathname - 현재 경로
 * @returns depth (null이면 HierarchicalSelector를 표시하지 않음)
 */
export const getDepthForPath = (pathname: string): SelectionDepth | null => {
  // 정확히 일치하는 경로 먼저 찾기
  const exactMatch = PAGE_DEPTH_CONFIG.find(config => config.path === pathname);
  if (exactMatch) {
    return exactMatch.depth;
  }

  // 정규식 패턴으로 찾기
  for (const config of PAGE_DEPTH_CONFIG) {
    const regex = new RegExp(`^${config.path}$`);
    if (regex.test(pathname)) {
      return config.depth;
    }
  }

  // 기본값 (device)
  return 'device';
};
